import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import AuthImage from '../../assets/cannabis4.avif';
import Signin from '../../components/LoginSignin/Signin';
import Login from '../../components/LoginSignin/Login';
import '../../components/LoginSignin/Login.css';

const estados = {
  'cuenta-confirmada': {
    type: 'success',
    text: 'Cuenta confirmada. Ya podés iniciar sesión.',
  },
  'token-invalido': {
    type: 'error',
    text: 'El enlace de confirmación no es válido o ya fue usado.',
  },
  'password-actualizada': {
    type: 'success',
    text: 'Contraseña actualizada. Iniciá sesión con tu nueva clave.',
  },
};

function PasswordResetRequest({ onBack }) {
  const [correo, setCorreo] = useState('');
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const pedirReset = async (event) => {
    event.preventDefault();
    setMessage('');
    setErrorMessage('');

    try {
      setLoading(true);
      const response = await fetch('/api/password-reset/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ correo }),
      });

      if (response.ok) {
        setMessage('Si el correo existe, te enviamos un enlace para crear una nueva contraseña.');
        setCorreo('');
        return;
      }

      setErrorMessage('No pudimos procesar la solicitud.');
    } catch (error) {
      console.error('Error solicitando reset:', error);
      setErrorMessage('No pudimos conectar con el servidor.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-panel">
      <div className="auth-heading">
        <p className="auth-kicker">Recuperación</p>
        <h1>Restablecé tu contraseña</h1>
      </div>
      <form className="auth-form" onSubmit={pedirReset}>
        <div className="form-group">
          <label htmlFor="reset-correo">Correo electrónico</label>
          <input
            autoComplete="email"
            id="reset-correo"
            onChange={(e) => setCorreo(e.target.value)}
            required
            type="email"
            value={correo}
          />
        </div>
        {message && <p className="auth-message success">{message}</p>}
        {errorMessage && <p className="auth-message error">{errorMessage}</p>}
        <button className="auth-submit" disabled={loading} type="submit">
          {loading ? 'Enviando...' : 'Enviar enlace'}
        </button>
      </form>
      <div className="auth-links">
        <button type="button" onClick={onBack}>Volver al login</button>
      </div>
    </div>
  );
}

function PasswordResetConfirm({ token }) {
  const [contrasena, setContrasena] = useState('');
  const [confirmarContrasena, setConfirmarContrasena] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const actualizarPassword = async (event) => {
    event.preventDefault();
    setErrorMessage('');

    if (contrasena !== confirmarContrasena) {
      setErrorMessage('Las contraseñas no coinciden.');
      return;
    }

    if (!/^(?=.*[a-zA-Z])(?=.*\d).{8,}$/.test(contrasena)) {
      setErrorMessage('La contraseña debe tener al menos 8 caracteres, una letra y un número.');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/password-reset/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, contraseña: contrasena }),
      });

      if (response.ok) {
        window.location.href = '/login?estado=password-actualizada';
        return;
      }

      const data = await response.json().catch(() => ({}));
      setErrorMessage(data.error || 'El enlace venció o no es válido.');
    } catch (error) {
      console.error('Error confirmando reset:', error);
      setErrorMessage('No pudimos conectar con el servidor.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-panel">
      <div className="auth-heading">
        <p className="auth-kicker">Nueva contraseña</p>
        <h1>Creá una clave segura</h1>
      </div>
      <form className="auth-form" onSubmit={actualizarPassword}>
        <div className="form-group">
          <label htmlFor="new-password">Contraseña</label>
          <input
            autoComplete="new-password"
            id="new-password"
            onChange={(e) => setContrasena(e.target.value)}
            required
            type="password"
            value={contrasena}
          />
        </div>
        <div className="form-group">
          <label htmlFor="confirm-new-password">Confirmar contraseña</label>
          <input
            autoComplete="new-password"
            id="confirm-new-password"
            onChange={(e) => setConfirmarContrasena(e.target.value)}
            required
            type="password"
            value={confirmarContrasena}
          />
        </div>
        {errorMessage && <p className="auth-message error">{errorMessage}</p>}
        <button className="auth-submit" disabled={loading || !token} type="submit">
          {loading ? 'Actualizando...' : 'Actualizar contraseña'}
        </button>
      </form>
    </div>
  );
}

function Begin() {
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const tipo = queryParams.get('tipo');
  const modo = queryParams.get('modo');
  const token = queryParams.get('token');
  const estado = queryParams.get('estado');
  const [view, setView] = useState(tipo === 'signin' ? 'signin' : 'login');

  useEffect(() => {
    if (modo === 'reset') {
      setView('reset-confirm');
      return;
    }

    if (tipo === 'signin') {
      setView('signin');
    }
  }, [modo, tipo]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch('/api/infocuentas', { credentials: 'include' });
        if (response.status === 200) {
          window.location.href = '/';
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    if (view !== 'reset-confirm') {
      fetchData();
    }
  }, [view]);

  const notice = estados[estado];

  const renderPanel = () => {
    if (view === 'signin') {
      return <Signin onToggle={() => setView('login')} />;
    }

    if (view === 'reset-request') {
      return <PasswordResetRequest onBack={() => setView('login')} />;
    }

    if (view === 'reset-confirm') {
      return <PasswordResetConfirm token={token} />;
    }

    return (
      <Login
        notice={notice}
        onForgotPassword={() => setView('reset-request')}
        onToggle={() => setView('signin')}
      />
    );
  };

  return (
    <main className="auth-page">
      <section className="auth-shell">
        <div className="left-section">{renderPanel()}</div>
        <div className="right-section" aria-hidden="true">
          <img className="loginsection" src={AuthImage} alt="" />
          <div className="auth-photo-copy">
            <p>Cannubis Store</p>
            <strong>Primera comunidad cannábica Argentina</strong>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Begin;
